/**
 * LCD Eligibility Checker - Hospice Eligibility Evaluation Tool
 * 
 * CMS-compliant guided form for evaluating patient eligibility for hospice care
 * based on Local Coverage Determination (LCD) criteria.
 * 
 * Features:
 * - Diagnosis category selection with ICD-10 auto-mapping
 * - Functional assessment score entry (KPS, PPS, FAST, NYHA)
 * - Clinical indicator checklist
 * - Real-time eligibility status display
 * - Save evaluation for audit trail
 */

import React, { useState, useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ClipboardCheck, Heart, Brain, Activity, Stethoscope,
  CheckCircle, XCircle, AlertTriangle, Info, Search,
  Loader2, Save, RefreshCw, FileText, User, Calendar,
  ChevronRight, ArrowRight, Shield, HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import FunctionalAssessmentHistory from "@/components/FunctionalAssessmentHistory";
import FunctionalStatusTrend from "@/components/FunctionalStatusTrend";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// LCD Category Icons
const CATEGORY_ICONS = {
  cancer: "🎗️",
  dementia: "🧠",
  heart_disease: "❤️",
  pulmonary: "🫁",
  renal: "🫘",
  liver: "🔶",
  als: "🧬",
  stroke: "⚡",
  hiv_aids: "🔬",
  debility: "📉"
};

// Functional Scale Descriptions
const SCALE_INFO = {
  kps: {
    name: "Karnofsky Performance Scale (KPS)",
    threshold: 70,
    description: "Measures functional status. Hospice threshold: ≤70",
    range: [0, 100],
    step: 10
  },
  pps: {
    name: "Palliative Performance Scale (PPS)",
    threshold: 70,
    description: "Measures palliative performance. Hospice threshold: ≤70%",
    range: [0, 100],
    step: 10
  },
  fast: {
    name: "Functional Assessment Staging (FAST)",
    threshold: "7c",
    description: "For dementia patients. Hospice threshold: Stage 7c+",
    stages: ["1", "2", "3", "4", "5", "6a", "6b", "6c", "6d", "6e", "7a", "7b", "7c", "7d", "7e", "7f"]
  },
  nyha: {
    name: "NYHA Classification",
    threshold: "IV",
    description: "For heart failure. Hospice threshold: Class IV",
    classes: ["I", "II", "III", "IV"]
  }
};

export default function LCDEligibilityChecker() {
  const { isDark } = useTheme();
  const [token, setToken] = useState(localStorage.getItem("token"));
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState(null);
  const [functionalScales, setFunctionalScales] = useState(null);
  
  // Form states
  const [patientName, setPatientName] = useState("");
  const [patientId, setPatientId] = useState("");
  const [icd10Code, setIcd10Code] = useState("");
  const [kpsScore, setKpsScore] = useState(70);
  const [ppsScore, setPpsScore] = useState(70);
  const [fastStage, setFastStage] = useState("");
  const [nyhaClass, setNyhaClass] = useState("");
  const [checkedIndicators, setCheckedIndicators] = useState({});
  const [checkedDocs, setCheckedDocs] = useState({});
  const [notes, setNotes] = useState("");
  
  // Evaluation states
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  
  // Step tracking
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Fetch LCD categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/lcd-criteria/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
        }
      } catch (error) {
        console.error("Failed to fetch LCD categories:", error);
        toast.error("Failed to load LCD categories");
      } finally {
        setLoading(false);
      }
    };

    const fetchScales = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lcd-criteria/functional-scales`);
        if (res.ok) {
          const data = await res.json();
          setFunctionalScales(data.scales);
        }
      } catch (error) {
        console.error("Failed to fetch functional scales:", error);
      }
    };

    fetchCategories();
    fetchScales();
  }, []);

  // Fetch category details when selected
  useEffect(() => {
    if (!selectedCategory) {
      setCategoryDetails(null);
      return;
    }

    const fetchDetails = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lcd-criteria/categories/${selectedCategory}`);
        if (res.ok) {
          const data = await res.json();
          setCategoryDetails(data);
          // Reset checked states
          setCheckedIndicators({});
          setCheckedDocs({});
        }
      } catch (error) {
        console.error("Failed to fetch category details:", error);
      }
    };

    fetchDetails();
  }, [selectedCategory]);

  // ICD-10 Lookup
  const lookupICD10 = async () => {
    if (!icd10Code.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/lcd-criteria/lookup-icd10/${icd10Code.trim()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.lcd_category) {
          setSelectedCategory(data.lcd_category);
          toast.success(`Matched to ${data.lcd_name} (${data.lcd_code})`);
        } else {
          toast.info("No specific LCD found. Consider 'Debility' category.");
        }
      }
    } catch (error) {
      toast.error("ICD-10 lookup failed");
    }
  };

  // Evaluate eligibility
  const evaluateEligibility = async () => {
    if (!selectedCategory) {
      toast.error("Please select a diagnosis category");
      return;
    }

    try {
      setEvaluating(true);
      
      // Build clinical data
      const clinicalData = {};
      Object.entries(checkedIndicators).forEach(([key, checked]) => {
        if (checked) clinicalData[key] = true;
      });
      Object.entries(checkedDocs).forEach(([key, checked]) => {
        if (checked) clinicalData[key] = true;
      });

      const res = await fetch(`${API_URL}/api/lcd-criteria/evaluate/comprehensive`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId || "evaluation-preview",
          primary_diagnosis_icd10: icd10Code || "unknown",
          kps_score: kpsScore,
          pps_score: ppsScore,
          fast_stage: fastStage || null,
          nyha_class: nyhaClass || null,
          clinical_indicators: Object.entries(checkedIndicators)
            .filter(([_, v]) => v)
            .map(([k]) => k),
          documentation_present: Object.entries(checkedDocs)
            .filter(([_, v]) => v)
            .map(([k]) => k)
        })
      });

      if (res.ok) {
        const result = await res.json();
        setEvaluationResult(result);
        setShowResultDialog(true);
      } else {
        toast.error("Evaluation failed");
      }
    } catch (error) {
      toast.error("Evaluation failed");
    } finally {
      setEvaluating(false);
    }
  };

  // Save evaluation
  const saveEvaluation = async () => {
    if (!evaluationResult || !patientId) {
      toast.error("Patient ID required to save evaluation");
      return;
    }

    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/lcd-criteria/save-evaluation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          diagnosis_category: selectedCategory,
          evaluation_result: evaluationResult,
          evaluator_id: "current-user",
          notes: notes
        })
      });

      if (res.ok) {
        toast.success("Evaluation saved successfully");
        setShowResultDialog(false);
      } else {
        toast.error("Failed to save evaluation");
      }
    } catch (error) {
      toast.error("Failed to save evaluation");
    } finally {
      setSaving(false);
    }
  };

  // Calculate completion percentage
  const getCompletionPercentage = () => {
    let completed = 0;
    if (selectedCategory) completed += 25;
    if (kpsScore || ppsScore) completed += 25;
    if (Object.values(checkedIndicators).some(v => v)) completed += 25;
    if (Object.values(checkedDocs).some(v => v)) completed += 25;
    return completed;
  };

  // Get functional threshold status
  const getFunctionalStatus = () => {
    if (selectedCategory === "dementia") {
      const fastOrder = ["7a", "7b", "7c", "7d", "7e", "7f"];
      const isHospiceEligible = fastStage && fastOrder.indexOf(fastStage.toLowerCase()) >= 2;
      return { eligible: isHospiceEligible, message: isHospiceEligible ? "FAST ≥7c" : "FAST <7c" };
    }
    if (selectedCategory === "heart_disease") {
      const isHospiceEligible = nyhaClass === "IV";
      return { eligible: isHospiceEligible, message: isHospiceEligible ? "NYHA IV" : `NYHA ${nyhaClass || "N/A"}` };
    }
    const meetsKPS = kpsScore <= 70;
    const meetsPPS = ppsScore <= 70;
    return { 
      eligible: meetsKPS || meetsPPS, 
      message: `KPS: ${kpsScore} / PPS: ${ppsScore}%`
    };
  };

  const functionalStatus = getFunctionalStatus();

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950" : "bg-gray-50"}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"} sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${isDark ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
                <ClipboardCheck className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  LCD Eligibility Checker
                </h1>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  CMS Hospice Eligibility Evaluation Tool
                </p>
              </div>
            </div>
            
            {/* Progress Indicator */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Evaluation Progress
                </p>
                <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {getCompletionPercentage()}% Complete
                </p>
              </div>
              <div className="w-32">
                <Progress value={getCompletionPercentage()} className="h-2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="col-span-2 space-y-6">
            {/* Step 1: Patient & Diagnosis Selection */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    selectedCategory ? "bg-emerald-500 text-white" : isDark ? "bg-slate-700 text-gray-400" : "bg-gray-200 text-gray-600"
                  }`}>
                    1
                  </div>
                  <div>
                    <CardTitle className="text-base">Patient & Diagnosis</CardTitle>
                    <CardDescription>Select LCD category or enter ICD-10 code</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Patient Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Patient Name</Label>
                    <Input 
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      placeholder="Enter patient name"
                      className={isDark ? "bg-slate-800 border-slate-700" : ""}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Patient ID / MRN</Label>
                    <Input 
                      value={patientId}
                      onChange={(e) => setPatientId(e.target.value)}
                      placeholder="Enter patient ID"
                      className={isDark ? "bg-slate-800 border-slate-700" : ""}
                    />
                  </div>
                </div>

                {/* ICD-10 Lookup */}
                <div>
                  <Label className="text-xs">ICD-10 Code (Optional)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={icd10Code}
                      onChange={(e) => setIcd10Code(e.target.value.toUpperCase())}
                      placeholder="e.g., C34.1, G30.9, I50.9"
                      className={`flex-1 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                    />
                    <Button variant="outline" onClick={lookupICD10}>
                      <Search className="w-4 h-4 mr-2" />
                      Lookup
                    </Button>
                  </div>
                </div>

                {/* LCD Category Selection */}
                <div>
                  <Label className="text-xs">LCD Diagnosis Category</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.key}
                        onClick={() => setSelectedCategory(cat.key)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          selectedCategory === cat.key
                            ? "border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/30"
                            : isDark 
                              ? "border-slate-700 bg-slate-800 hover:border-slate-600" 
                              : "border-gray-200 bg-gray-50 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-2xl">{CATEGORY_ICONS[cat.key]}</span>
                        <p className={`text-xs mt-1 font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {cat.name.split("/")[0].split("(")[0].trim()}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Functional Assessment */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    functionalStatus.eligible ? "bg-emerald-500 text-white" : isDark ? "bg-slate-700 text-gray-400" : "bg-gray-200 text-gray-600"
                  }`}>
                    2
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base">Functional Assessment</CardTitle>
                    <CardDescription>Enter functional status scores</CardDescription>
                  </div>
                  {selectedCategory && (
                    <Badge className={functionalStatus.eligible 
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : "bg-amber-500/20 text-amber-400 border-amber-500/30"
                    }>
                      {functionalStatus.message}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* KPS/PPS Scores */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">Karnofsky Performance Scale (KPS)</Label>
                      <span className={`text-2xl font-bold ${
                        kpsScore <= 70 ? "text-emerald-400" : "text-amber-400"
                      }`}>
                        {kpsScore}
                      </span>
                    </div>
                    <Slider
                      value={[kpsScore]}
                      onValueChange={(v) => setKpsScore(v[0])}
                      min={0}
                      max={100}
                      step={10}
                      className="mt-2"
                    />
                    <div className="flex justify-between mt-1">
                      <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>0</span>
                      <span className={`text-xs ${kpsScore <= 70 ? "text-emerald-400" : "text-amber-400"}`}>
                        Threshold: ≤70
                      </span>
                      <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>100</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">Palliative Performance Scale (PPS)</Label>
                      <span className={`text-2xl font-bold ${
                        ppsScore <= 70 ? "text-emerald-400" : "text-amber-400"
                      }`}>
                        {ppsScore}%
                      </span>
                    </div>
                    <Slider
                      value={[ppsScore]}
                      onValueChange={(v) => setPpsScore(v[0])}
                      min={0}
                      max={100}
                      step={10}
                      className="mt-2"
                    />
                    <div className="flex justify-between mt-1">
                      <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>0%</span>
                      <span className={`text-xs ${ppsScore <= 70 ? "text-emerald-400" : "text-amber-400"}`}>
                        Threshold: ≤70%
                      </span>
                      <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>100%</span>
                    </div>
                  </div>
                </div>

                {/* FAST Stage (for Dementia) */}
                {selectedCategory === "dementia" && (
                  <div>
                    <Label className="text-xs">FAST Stage (Dementia)</Label>
                    <Select value={fastStage} onValueChange={setFastStage}>
                      <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                        <SelectValue placeholder="Select FAST stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {SCALE_INFO.fast.stages.map((stage) => (
                          <SelectItem key={stage} value={stage}>
                            Stage {stage} {stage.startsWith("7") && stage >= "7c" && "(Hospice Eligible)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      Hospice threshold: Stage 7c or beyond
                    </p>
                  </div>
                )}

                {/* NYHA Class (for Heart Disease) */}
                {selectedCategory === "heart_disease" && (
                  <div>
                    <Label className="text-xs">NYHA Classification (Heart Failure)</Label>
                    <Select value={nyhaClass} onValueChange={setNyhaClass}>
                      <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                        <SelectValue placeholder="Select NYHA class" />
                      </SelectTrigger>
                      <SelectContent>
                        {SCALE_INFO.nyha.classes.map((cls) => (
                          <SelectItem key={cls} value={cls}>
                            Class {cls} {cls === "IV" && "(Hospice Eligible)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      Hospice threshold: Class IV - symptoms at rest
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 3: Clinical Indicators */}
            {categoryDetails && (
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      Object.values(checkedIndicators).some(v => v) 
                        ? "bg-emerald-500 text-white" 
                        : isDark ? "bg-slate-700 text-gray-400" : "bg-gray-200 text-gray-600"
                    }`}>
                      3
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-base">Clinical Indicators</CardTitle>
                      <CardDescription>Check all indicators present in patient</CardDescription>
                    </div>
                    <Badge variant="outline">
                      {Object.values(checkedIndicators).filter(v => v).length} / {categoryDetails.clinical_indicators?.length || 0}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {categoryDetails.clinical_indicators?.map((indicator, idx) => {
                      const key = indicator.toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 30);
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            checkedIndicators[key]
                              ? "border-emerald-500 bg-emerald-500/10"
                              : isDark ? "border-slate-700 bg-slate-800" : "border-gray-200 bg-gray-50"
                          }`}
                          onClick={() => setCheckedIndicators(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }))}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox 
                              checked={checkedIndicators[key] || false}
                              className="mt-0.5"
                            />
                            <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {indicator}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Documentation */}
            {categoryDetails && (
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      Object.values(checkedDocs).some(v => v) 
                        ? "bg-emerald-500 text-white" 
                        : isDark ? "bg-slate-700 text-gray-400" : "bg-gray-200 text-gray-600"
                    }`}>
                      4
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-base">Required Documentation</CardTitle>
                      <CardDescription>Verify documentation is present</CardDescription>
                    </div>
                    <Badge variant="outline">
                      {Object.values(checkedDocs).filter(v => v).length} / {categoryDetails.required_documentation?.length || 0}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categoryDetails.required_documentation?.map((doc, idx) => {
                      const key = doc.toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 30);
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            checkedDocs[key]
                              ? "border-emerald-500 bg-emerald-500/10"
                              : isDark ? "border-slate-700 bg-slate-800" : "border-gray-200 bg-gray-50"
                          }`}
                          onClick={() => setCheckedDocs(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }))}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox 
                              checked={checkedDocs[key] || false}
                            />
                            <FileText className={`w-4 h-4 ${checkedDocs[key] ? "text-emerald-400" : isDark ? "text-gray-500" : "text-gray-400"}`} />
                            <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {doc}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Evaluate Button */}
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setSelectedCategory(null);
                  setKpsScore(70);
                  setPpsScore(70);
                  setFastStage("");
                  setNyhaClass("");
                  setCheckedIndicators({});
                  setCheckedDocs({});
                  setEvaluationResult(null);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button 
                onClick={evaluateEligibility}
                disabled={!selectedCategory || evaluating}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {evaluating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                )}
                Evaluate Eligibility
              </Button>
            </div>
          </div>

          {/* Right Column - LCD Criteria Reference */}
          <div className="space-y-6">
            {/* Selected Category Info */}
            {categoryDetails ? (
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{CATEGORY_ICONS[selectedCategory]}</span>
                    <div>
                      <CardTitle className="text-base">{categoryDetails.name}</CardTitle>
                      <Badge variant="outline" className="mt-1">{categoryDetails.code}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {categoryDetails.description}
                  </p>
                  
                  <Accordion type="single" collapsible className="mt-4">
                    <AccordionItem value="criteria">
                      <AccordionTrigger className="text-sm">
                        <span className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-blue-400" />
                          Eligibility Criteria
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className={`text-xs space-y-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {categoryDetails.criteria?.map((criterion, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <ChevronRight className="w-3 h-3 mt-0.5 text-emerald-400 flex-shrink-0" />
                              {criterion}
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="icd10">
                      <AccordionTrigger className="text-sm">
                        <span className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-purple-400" />
                          ICD-10 Codes
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-wrap gap-1">
                          {categoryDetails.icd10_codes?.map((code, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {code}
                            </Badge>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            ) : (
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardContent className="py-12 text-center">
                  <HelpCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                  <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Select a diagnosis category to view LCD criteria
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Quick Reference */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  Hospice Eligibility Quick Reference
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                  <p className={`text-xs font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    General Functional Thresholds
                  </p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className={isDark ? "text-gray-400" : "text-gray-600"}>KPS/PPS</span>
                      <span className="text-emerald-400 font-medium">≤70</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? "text-gray-400" : "text-gray-600"}>FAST (Dementia)</span>
                      <span className="text-emerald-400 font-medium">≥7c</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? "text-gray-400" : "text-gray-600"}>NYHA (Heart)</span>
                      <span className="text-emerald-400 font-medium">Class IV</span>
                    </div>
                  </div>
                </div>

                <div className={`p-3 rounded-lg border-l-4 border-l-amber-500 ${isDark ? "bg-amber-500/10" : "bg-amber-50"}`}>
                  <p className={`text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                    <strong>Note:</strong> Clinical judgment remains essential. 
                    These criteria are guidelines, not absolute requirements.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Patient Functional History - Shows when patient ID is entered */}
            {patientId && (
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-400" />
                    Patient Functional History
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {patientName || "Patient"}'s assessment trends
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Compact Trend Chart */}
                  <FunctionalStatusTrend
                    patientId={patientId}
                    patientName={patientName}
                    isDark={isDark}
                    compact={true}
                  />
                  
                  {/* Compact History */}
                  <FunctionalAssessmentHistory
                    patientId={patientId}
                    patientName={patientName}
                    isDark={isDark}
                    compact={true}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Evaluation Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {evaluationResult?.hospice_eligible ? (
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              )}
              Eligibility Evaluation Result
            </DialogTitle>
            <DialogDescription>
              {patientName || "Patient"} - {categoryDetails?.name}
            </DialogDescription>
          </DialogHeader>
          
          {evaluationResult && (
            <div className="space-y-4">
              {/* Overall Status */}
              <div className={`p-4 rounded-lg text-center ${
                evaluationResult.hospice_eligible
                  ? "bg-emerald-500/10 border border-emerald-500/30"
                  : "bg-amber-500/10 border border-amber-500/30"
              }`}>
                <p className={`text-2xl font-bold ${
                  evaluationResult.hospice_eligible ? "text-emerald-400" : "text-amber-400"
                }`}>
                  {evaluationResult.hospice_eligible ? "HOSPICE ELIGIBLE" : "CRITERIA NOT FULLY MET"}
                </p>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {evaluationResult.primary_diagnosis?.lcd_name} ({evaluationResult.primary_diagnosis?.lcd_category})
                </p>
              </div>

              {/* Functional Assessment */}
              <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <p className="text-sm font-medium mb-2">Functional Assessment</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-500"}>KPS Score</p>
                    <p className="font-medium">{evaluationResult.functional_assessment?.kps_score || "N/A"}</p>
                  </div>
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-500"}>PPS Score</p>
                    <p className="font-medium">{evaluationResult.functional_assessment?.pps_score || "N/A"}%</p>
                  </div>
                  <div>
                    <p className={isDark ? "text-gray-500" : "text-gray-500"}>Threshold Met</p>
                    <Badge className={
                      evaluationResult.functional_assessment?.meets_functional_threshold
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-amber-500/20 text-amber-400"
                    }>
                      {evaluationResult.functional_assessment?.meets_functional_threshold ? "Yes" : "No"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              {evaluationResult.recommendations?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Recommendations</p>
                  <ul className="space-y-2">
                    {evaluationResult.recommendations.map((rec, idx) => (
                      <li key={idx} className={`text-sm flex items-start gap-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        <ArrowRight className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label className="text-xs">Evaluation Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this evaluation..."
                  className={`mt-1 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={saveEvaluation} 
              disabled={saving || !patientId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Evaluation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
